import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import { toast } from "sonner";
import {
  Phone, CheckCircle2, XCircle, Clock, MapPin, ArrowRight, ArrowLeft,
  RefreshCw, RotateCcw, MailCheck, MailWarning, Sparkles, ShieldAlert, Trash2, FlaskConical,
  ChevronLeft, ChevronRight, CalendarRange, X, AlertTriangle, ChevronDown, CreditCard, LifeBuoy,
} from "lucide-react";
import { formatTime12 } from "@/shared/lib/format";
import RecoverPaidRideDialog from "@/apps/admin/components/RecoverPaidRideDialog";

/**
 * AdminOps — private mobile-first ops panel. Pivot by date (Yesterday · Today ·
 * Tomorrow · custom). Filter by status (All / Completed / Pending / Unpaid).
 * Per-trip actions: tap-to-call · complete · cancel. Per-route actions: mark
 * test · delete (with paid-route confirmation).
 */
const FILTERS = [
  { id: "all",       label: "All" },
  { id: "scheduled", label: "Pending" },
  { id: "completed", label: "Completed" },
  { id: "unpaid",    label: "Unpaid" },
];

export default function AdminOps() {
  const { user, loading: authLoading } = useAuth();
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const userFilter = sp.get("user") || "";
  const filterRiderName = location.state?.rider_name || "";
  const [date, setDate] = useState(() => isoToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");
  const [now, setNow] = useState(new Date());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { count, paidCount } | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [detailTrip, setDetailTrip] = useState(null); // currently-open ride drawer
  const [reconciling, setReconciling] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);

  const clearUserFilter = () => {
    const next = new URLSearchParams(sp);
    next.delete("user");
    setSp(next, { replace: true });
  };

  const isToday = date === isoToday();

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const path = isToday ? "/admin/ops/today" : `/admin/ops/day?date=${date}`;
      const { data } = await api.get(path);
      setData(data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Failed to load ops data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, isToday]);

  useEffect(() => { if (!authLoading && user) load(); }, [authLoading, user, load]);

  // Clear bulk selection whenever the displayed dataset changes (date/filter swap).
  useEffect(() => { setSelectedIds(new Set()); }, [date, filter, userFilter]);

  // 30s tick for the next-ride countdown only when viewing today.
  useEffect(() => {
    if (!isToday) return;
    const t = setInterval(() => { setNow(new Date()); load(true); }, 30000);
    return () => clearInterval(t);
  }, [isToday, load]);

  const completeTrip = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/ops/trips/${id}/complete`); toast.success("Ride marked completed"); await load(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };
  const cancelTrip = async (id) => {
    if (!window.confirm("Cancel this ride?")) return;
    setBusy(id);
    try { await api.post(`/admin/ops/trips/${id}/cancel`); toast.success("Ride cancelled"); await load(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };
  // Admin override — revert a completed/cancelled trip back to scheduled.
  const resetTrip = async (id) => {
    if (!window.confirm("Revert this ride back to scheduled?")) return;
    setBusy(id);
    try { await api.post(`/admin/ops/trips/${id}/reset`); toast.success("Ride reverted to scheduled"); await load(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };
  const flagAsTest = async (trip) => {
    setBusy(trip.id);
    try {
      await api.post(`/admin/ops/routes/${trip.route_id}/test-flag?value=${!trip.is_test}`);
      toast.success(trip.is_test ? "Removed test flag" : "Marked as test data");
      await load(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };
  const deleteRoute = async (trip) => {
    const paid = trip.payment_status === "paid";
    const msg = paid
      ? "⚠ This booking has a COMPLETED payment. Deleting removes the route + all its trips + payment records. This cannot be undone. Continue?"
      : "Delete this booking and all its trips and payment records? This cannot be undone.";
    if (!window.confirm(msg)) return;
    setBusy(trip.id);
    try {
      await api.delete(`/admin/ops/routes/${trip.route_id}`);
      toast.success("Booking deleted");
      await load(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(null); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const openBulkConfirm = () => {
    const trips = (data?.trips || []).filter((t) => selectedIds.has(t.id));
    const paidCount = trips.filter((t) => t.payment_status === "paid").length;
    setConfirmDelete({ count: trips.length, paidCount });
  };
  const performBulkDelete = async () => {
    if (selectedIds.size === 0) { setConfirmDelete(null); return; }
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const { data: res } = await api.post("/admin/ops/trips/bulk-delete", { trip_ids: ids });
      toast.success(`Deleted ${res.trips} ride${res.trips === 1 ? "" : "s"}`);
      clearSelection();
      setConfirmDelete(null);
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };
  const deleteAllTestData = async () => {
    if (!window.confirm("Delete every route flagged as test data? This cascades through trips and payment records.")) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/ops/routes/bulk-delete", { test_only: true });
      toast.success(`Removed ${res.routes} test route${res.routes === 1 ? "" : "s"} (${res.trips} trips)`);
      clearSelection();
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test data wipe failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const reconcileStripe = async () => {
    if (!window.confirm("Backfill payment status from Stripe?\n\nThis fetches every locally-unpaid transaction and checks Stripe directly. Used when webhooks weren't delivered.")) return;
    setReconciling(true);
    try {
      const { data: res } = await api.post("/admin/ops/reconcile-stripe", {});
      const msg = res.activated > 0
        ? `Activated ${res.activated} routes (${res.paid_in_stripe} were paid in Stripe of ${res.checked} checked).`
        : res.checked === 0
          ? "Nothing to reconcile — all transactions already marked paid."
          : `Checked ${res.checked} · ${res.paid_in_stripe} paid in Stripe · ${res.activated} newly activated.`;
      toast.success(msg);
      if (res.errors?.length) toast.error(`${res.errors.length} session lookup error${res.errors.length === 1 ? "" : "s"} — see backend logs.`);
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const nextTrip = useMemo(() => {
    if (!isToday || !data?.next_trip_id) return null;
    return (data.trips || []).find((t) => t.id === data.next_trip_id) || null;
  }, [isToday, data]);

  const timeUntil = useMemo(() => {
    if (!nextTrip) return null;
    const [hh, mm] = nextTrip.pickup_time.split(":").map(Number);
    const target = new Date(now);
    target.setHours(hh, mm, 0, 0);
    const diffMs = target.getTime() - now.getTime();
    if (diffMs <= 0) return "starting now";
    const totalMin = Math.round(diffMs / 60000);
    if (totalMin < 60) return `in ${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `in ${h}h ${m}m`;
  }, [nextTrip, now]);

  const filtered = useMemo(() => {
    let all = data?.trips || [];
    if (userFilter) all = all.filter((t) => t.user_id === userFilter);
    if (filter === "all") return all;
    if (filter === "unpaid") return all.filter((t) => t.payment_status !== "paid");
    return all.filter((t) => t.status === filter);
  }, [data, filter, userFilter]);

  // Re-sync the open detail drawer with the latest trip payload after refetch
  // (so action buttons inside the drawer reflect the new server state).
  useEffect(() => {
    if (!detailTrip) return;
    const fresh = (data?.trips || []).find((t) => t.id === detailTrip.id);
    if (fresh) setDetailTrip(fresh);
    else setDetailTrip(null); // ride was deleted/cancelled out of view
  }, [data, detailTrip]);

  // Pending ride count distinguishes RIDES (unique route_ids) vs LEGS (trips).
  // Surfaces the truth that one rider's round-trip is one booking, not two.
  const counts = useMemo(() => {
    const cur = filtered;
    const scheduled = cur.filter((t) => t.status === "scheduled");
    const completed = cur.filter((t) => t.status === "completed");
    const cancelled = cur.filter((t) => t.status === "cancelled");
    const uniq = (rows) => new Set(rows.map((t) => t.route_id)).size;
    return {
      scheduled_legs: scheduled.length,
      scheduled_rides: uniq(scheduled),
      completed_legs: completed.length,
      completed_rides: uniq(completed),
      cancelled_legs: cancelled.length,
      cancelled_rides: uniq(cancelled),
      total_legs: cur.length,
      total_rides: uniq(cur),
    };
  }, [filtered]);

  if (authLoading || loading) {
    return (
      <>
        <div className="min-h-[40vh] flex items-center justify-center label-xs text-[#515E7B]" data-testid="ops-loading">Loading ops…</div>
      </>
    );
  }
  if (!user || forbidden) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="ops-forbidden">
        <ShieldAlert size={28} className="mx-auto text-[#ed6500] mb-4" strokeWidth={1.6} />
        <div className="font-serif text-2xl mb-2">Not permitted</div>
        <p className="text-sm text-[#515E7B]">This dashboard is restricted to the Link operations account.</p>
      </div>
    );
  }

  const trips = filtered;
  const blocks = data?.blocks || [];
  const health = data?.email_health;
  const healthBad = !!health?.last_failure;

  // Card click opens the drawer; action icons stop propagation so they don't trigger it.
  const openDetail = (t) => setDetailTrip(t);
  const closeDetail = () => setDetailTrip(null);

  return (
    <>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8" data-testid="admin-ops">
        <header className="pb-4 sm:pb-5 mb-4 sm:mb-6 border-b hairline flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="label-xs text-[#ed6500]">Link Ops</div>
            <h1 className="font-serif text-2xl sm:text-3xl leading-none mt-1 truncate" data-testid="ops-date-label">
              {new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setRecoverOpen(true)}
              className="p-2 text-[#515E7B] hover:text-[#ed6500]"
              title="Recover a paid ride that never activated"
              aria-label="Recover paid ride"
              data-testid="ops-recover-paid-btn"
            >
              <LifeBuoy size={18} />
            </button>
            <button
              onClick={reconcileStripe}
              disabled={reconciling}
              className="p-2 text-[#515E7B] hover:text-[#ed6500] disabled:opacity-50"
              title="Backfill payment status from Stripe"
              aria-label="Reconcile Stripe payments"
              data-testid="ops-reconcile-stripe-btn"
            >
              <CreditCard size={18} className={reconciling ? "animate-pulse" : ""} />
            </button>
            <button
              onClick={() => load(false)}
              className="p-2 text-[#515E7B] hover:text-[#ed6500]"
              aria-label="Refresh"
              data-testid="ops-refresh-btn"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </header>

        {/* User filter banner (set via /admin/customers "View rides") */}
        {userFilter && (
          <div className="mb-4 sm:mb-5 border-l-2 border-[#5c97f7] bg-white p-3 flex items-center justify-between gap-3" data-testid="ops-user-filter-banner">
            <div className="text-xs text-[#515E7B]">
              Filtered to one rider · <span className="text-[#0A1128] font-semibold truncate">{filterRiderName || (filtered[0]?.rider_name) || (data?.trips || []).find(t => t.user_id === userFilter)?.rider_name || "this user"}</span>
            </div>
            <button onClick={clearUserFilter} className="text-[#515E7B] hover:text-[#0A1128] p-1" aria-label="Clear filter" data-testid="ops-clear-user-filter">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Date navigation */}
        <div className="mb-4 sm:mb-5 flex items-center gap-2 flex-wrap" data-testid="ops-date-nav">
          <button
            onClick={() => setDate(shiftIso(date, -1))}
            className="p-2 text-[#515E7B] hover:text-[#ed6500] border hairline bg-white"
            aria-label="Previous day"
            data-testid="ops-prev-day"
          >
            <ChevronLeft size={14} />
          </button>
          {[
            { id: "yesterday", label: "Yesterday", iso: shiftIso(isoToday(), -1) },
            { id: "today",     label: "Today",     iso: isoToday() },
            { id: "tomorrow",  label: "Tomorrow",  iso: shiftIso(isoToday(), +1) },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => setDate(p.iso)}
              className={`px-3 py-2 text-[10px] tracking-widest uppercase border hairline transition-colors ${
                date === p.iso ? "bg-[#0A1128] text-[#F9F7F4]" : "bg-white text-[#515E7B] hover:text-[#ed6500]"
              }`}
              data-testid={`ops-quick-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="text-xs px-2 py-2 border hairline bg-white text-[#0A1128]"
            data-testid="ops-date-picker"
          />
          <button
            onClick={() => setDate(shiftIso(date, +1))}
            className="p-2 text-[#515E7B] hover:text-[#ed6500] border hairline bg-white"
            aria-label="Next day"
            data-testid="ops-next-day"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Status filters */}
        <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="ops-filter-bar">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-[10px] tracking-widest uppercase border hairline transition-colors ${
                filter === f.id ? "bg-[#ed6500] text-white border-[#ed6500]" : "bg-white text-[#515E7B] hover:text-[#0A1128]"
              }`}
              data-testid={`ops-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Selection toolbar — Select All + Delete All Test Data */}
        {trips.length > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="ops-selection-toolbar">
            <label className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#515E7B] cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#ed6500]"
                checked={trips.length > 0 && trips.every((t) => selectedIds.has(t.id))}
                onChange={(e) => {
                  if (e.target.checked) setSelectedIds(new Set(trips.map((t) => t.id)));
                  else clearSelection();
                }}
                data-testid="ops-select-all"
              />
              Select all on this date
            </label>
            <button
              onClick={deleteAllTestData}
              disabled={bulkBusy}
              className="text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors"
              title="Delete every route flagged as test data"
              data-testid="ops-delete-test-data"
            >
              <FlaskConical size={11} /> Delete all test data
            </button>
          </div>
        )}

        {/* Availability summary for the day */}
        {blocks.length > 0 && (
          <div className="mb-5 border-l-2 border-[#ed6500] bg-white p-3 text-xs text-[#515E7B] flex items-center gap-2" data-testid="ops-day-blocks">
            <CalendarRange size={12} className="text-[#ed6500] shrink-0" />
            <span>
              {blocks.map((b) => b.slot === "all" ? "Whole day blocked" : `${b.slot.toUpperCase()} blocked`).join(" · ")}
            </span>
          </div>
        )}

        {nextTrip ? (
          <div className="mb-6 bg-[#0A1128] text-[#F9F7F4] p-5 sm:p-6" data-testid="ops-next-ride">
            <div className="flex items-center gap-2 label-xs text-[#5c97f7]">
              <Clock size={12} strokeWidth={2} />
              Next ride · {timeUntil}
            </div>
            <div className="font-serif text-3xl sm:text-4xl leading-none mt-2" data-testid="ops-next-time">{formatTime12(nextTrip.pickup_time)}</div>
            <div className="mt-3 text-sm text-[#F9F7F4]/90 truncate">{nextTrip.rider_name || "—"}</div>
            <div className="text-xs text-[#F9F7F4]/60 mt-1 truncate">
              {nextTrip.direction === "outbound"
                ? <><ArrowRight size={10} className="inline mb-0.5" /> {nextTrip.pickup_address} → {nextTrip.dropoff_address}</>
                : <><ArrowLeft size={10} className="inline mb-0.5" /> {nextTrip.pickup_address} → {nextTrip.dropoff_address}</>}
            </div>
          </div>
        ) : isToday && (data?.trips || []).length > 0 ? (
          <div className="mb-6 border hairline bg-white p-4 text-sm text-[#515E7B]" data-testid="ops-no-upcoming">
            No more upcoming rides today.
          </div>
        ) : null}

        <section className="space-y-3" data-testid="ops-trips-list">
          <div className="flex items-center justify-between gap-3" data-testid="ops-trips-header">
            <div className="label-xs text-[#515E7B]">
              {filter === "all" ? "All rides" : FILTERS.find((f) => f.id === filter).label} · {counts.total_rides} ride{counts.total_rides === 1 ? "" : "s"}
              <span className="text-[#515E7B]/70"> ({counts.total_legs} leg{counts.total_legs === 1 ? "" : "s"})</span>
            </div>
            {filter === "all" && counts.scheduled_rides > 0 && (
              <div className="text-[10px] tracking-widest uppercase text-[#ed6500]" data-testid="ops-pending-summary">
                Pending: {counts.scheduled_rides} ride{counts.scheduled_rides === 1 ? "" : "s"} ({counts.scheduled_legs} leg{counts.scheduled_legs === 1 ? "" : "s"})
              </div>
            )}
          </div>
          {trips.length === 0 ? (
            <div className="border hairline bg-white p-6 text-center text-sm text-[#515E7B]" data-testid="ops-empty">
              {(data?.trips || []).length === 0 ? "No rides on this date." : "Nothing matches that filter."}
            </div>
          ) : trips.map((t) => (
            <TripCard
              key={t.id}
              trip={t}
              highlighted={isToday && t.id === data?.next_trip_id}
              busy={busy === t.id}
              selected={selectedIds.has(t.id)}
              onSelect={toggleSelect}
              onComplete={() => completeTrip(t.id)}
              onCancel={() => cancelTrip(t.id)}
              onReset={() => resetTrip(t.id)}
              onTestFlag={() => flagAsTest(t)}
              onDelete={() => deleteRoute(t)}
              onOpen={() => openDetail(t)}
            />
          ))}
        </section>

        {health && (
          <section className="mt-10 border hairline bg-white p-4" data-testid="ops-email-health">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 label-xs">
                {healthBad ? <><MailWarning size={14} className="text-[#ed6500]" /> Email · needs attention</>
                           : <><MailCheck size={14} className="text-[#5c97f7]" /> Email · healthy</>}
              </div>
              <div className="text-[10px] text-[#515E7B]">{health.totals.ok} sent · {health.totals.failed} failed</div>
            </div>
            <div className="text-[11px] text-[#515E7B] mb-3 truncate">From: <span className="text-[#0A1128]">{health.sender_from}</span></div>
            <div className="divide-y divide-[#E5E1D8] border-t hairline">
              {(health.recent || []).map((ev, i) => (
                <div key={ev.id || i} className="py-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {ev.ok ? <MailCheck size={12} className="text-[#5c97f7] shrink-0" /> : <MailWarning size={12} className="text-[#ed6500] shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-xs truncate">{ev.subject}</div>
                      <div className="text-[10px] text-[#515E7B] truncate">{(ev.to || []).join(", ")}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-[#515E7B] whitespace-nowrap">
                    {new Date(ev.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              ))}
              {(!health.recent || health.recent.length === 0) && (
                <div className="py-3 text-center text-xs text-[#515E7B]">No email activity yet.</div>
              )}
            </div>
          </section>
        )}

        {/* Padding for sticky bar so content above isn't hidden */}
        {selectedIds.size > 0 && <div className="h-20" aria-hidden="true" />}
      </div>

      {/* Sticky bulk-action bar */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A1128] text-[#F9F7F4] border-t border-[#ed6500]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="ops-bulk-action-bar"
        >
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="text-xs">
              <span className="font-semibold" data-testid="ops-bulk-count">{selectedIds.size}</span>
              <span className="text-[#F9F7F4]/70"> selected</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSelection}
                disabled={bulkBusy}
                className="text-[10px] tracking-widest uppercase text-[#F9F7F4]/70 hover:text-[#F9F7F4] px-3 py-2 disabled:opacity-50"
                data-testid="ops-bulk-clear"
              >
                Clear
              </button>
              <button
                onClick={openBulkConfirm}
                disabled={bulkBusy}
                className="text-[10px] tracking-widest uppercase font-semibold bg-[#ed6500] text-white px-4 py-2 hover:bg-[#d65a00] disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
                data-testid="ops-bulk-delete"
              >
                <Trash2 size={12} /> Delete selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-[#0A1128]/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !bulkBusy && setConfirmDelete(null)}
          data-testid="ops-bulk-confirm-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-[#F9F7F4] border-t-2 sm:border border-[#ed6500] sm:rounded-none p-6 sm:p-8"
            style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
          >
            <div className="label-xs text-[#ed6500] mb-2">Confirm bulk delete</div>
            <h2 className="font-serif text-2xl leading-tight text-[#0A1128]" data-testid="ops-bulk-confirm-title">
              Delete {confirmDelete.count} {confirmDelete.count === 1 ? "booking" : "bookings"}?
            </h2>
            {confirmDelete.paidCount > 0 && (
              <div className="mt-4 border-l-2 border-[#ed6500] bg-white p-3 flex items-start gap-2 text-xs text-[#515E7B]" data-testid="ops-bulk-paid-warning">
                <AlertTriangle size={14} className="text-[#ed6500] shrink-0 mt-0.5" />
                <span>
                  <span className="text-[#0A1128] font-semibold">{confirmDelete.paidCount}</span> of these bookings have completed payments. Deleting will remove the trip records, but Stripe charges remain on the customer's statement.
                </span>
              </div>
            )}
            <p className="mt-4 text-sm text-[#515E7B]">This action cannot be undone.</p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={bulkBusy}
                className="text-[10px] tracking-widest uppercase text-[#515E7B] hover:text-[#0A1128] px-4 py-2.5 disabled:opacity-50"
                data-testid="ops-bulk-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={performBulkDelete}
                disabled={bulkBusy}
                className="text-[10px] tracking-widest uppercase font-semibold bg-[#ed6500] text-white px-5 py-2.5 hover:bg-[#d65a00] disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
                data-testid="ops-bulk-confirm-delete"
              >
                <Trash2 size={12} /> {bulkBusy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ride detail drawer */}
      <RideDetailDrawer
        trip={detailTrip}
        busy={!!busy}
        onClose={closeDetail}
        onComplete={async (id) => { await completeTrip(id); closeDetail(); }}
        onCancel={async (id) => { await cancelTrip(id); closeDetail(); }}
        onReset={async (id) => { await resetTrip(id); closeDetail(); }}
      />

      {/* Recover Paid Ride dialog */}
      <RecoverPaidRideDialog
        open={recoverOpen}
        onClose={() => setRecoverOpen(false)}
        onSuccess={() => { load(true); }}
      />
    </>
  );
}

/* =====================  STATUS TOGGLE BUTTONS  ===================== */
/**
 * Renders the right complete/cancel/reset actions based on current trip
 * status.  Instead of showing all three buttons permanently, we show
 * exactly the transitions that make sense for the current state:
 *   · scheduled → Complete | Cancel
 *   · completed → Revert to scheduled
 *   · cancelled → Revert to scheduled
 */
function StatusToggleButtons({ status, busy, tripId, onComplete, onCancel, onReset }) {
  if (status === "scheduled") {
    return (
      <>
        <button
          onClick={onComplete} disabled={busy}
          title="Mark completed"
          className="inline-flex items-center justify-center w-9 h-9 border hairline text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white disabled:opacity-40 transition-colors"
          aria-label="Mark completed"
          data-testid={`ops-trip-complete-${tripId}`}
        >
          <CheckCircle2 size={14} />
        </button>
        <button
          onClick={onCancel} disabled={busy}
          title="Cancel ride"
          className="inline-flex items-center justify-center w-9 h-9 border hairline text-[#ed6500] hover:bg-[#ed6500] hover:text-white disabled:opacity-40 transition-colors"
          aria-label="Cancel ride"
          data-testid={`ops-trip-cancel-${tripId}`}
        >
          <XCircle size={14} />
        </button>
      </>
    );
  }
  // completed | cancelled → only expose "revert"
  return (
    <button
      onClick={onReset} disabled={busy}
      title="Revert to scheduled"
      className="inline-flex items-center justify-center w-9 h-9 border hairline text-[#515E7B] hover:bg-[#0A1128] hover:text-white disabled:opacity-40 transition-colors"
      aria-label="Revert to scheduled"
      data-testid={`ops-trip-reset-${tripId}`}
    >
      <RotateCcw size={14} />
    </button>
  );
}

function TripCard({ trip, highlighted = false, readOnlyActions = false, busy = false, selected = false, onSelect, onComplete, onCancel, onReset, onTestFlag, onDelete, onOpen }) {
  const t = trip;
  const baseStyles = t.status === "scheduled" ? "bg-white"
    : t.status === "completed" ? "bg-[#F9F7F4] opacity-80"
    : "bg-[#F9F7F4] opacity-60";
  // Action icons + checkbox stop the click from bubbling so they don't trigger
  // the card-level open-drawer handler.
  const stop = (e) => e.stopPropagation();
  return (
    <div
      className={`${baseStyles} border hairline p-4 ${highlighted ? "border-l-2 border-l-[#ed6500]" : ""} ${selected ? "ring-1 ring-[#ed6500]" : ""} ${onOpen ? "cursor-pointer hover:border-[#ed6500] active:bg-[#F9F7F4] transition-colors" : ""}`}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen ? () => onOpen(t) : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(t); } } : undefined}
      data-testid={`ops-trip-${t.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        {onSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(t.id)}
            onClick={stop}
            className="mt-1 w-4 h-4 accent-[#ed6500] shrink-0 cursor-pointer"
            aria-label={`Select ${t.rider_name || "ride"} ${t.pickup_time}`}
            data-testid={`ops-trip-select-${t.id}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-[#515E7B] flex-wrap">
            <span className="font-serif text-2xl leading-none text-[#0A1128]" data-testid={`ops-trip-time-${t.id}`}>
              {formatTime12(t.pickup_time)}
            </span>
            <span className="label-xs">
              {t.direction === "outbound"
                ? <><ArrowRight size={10} className="inline" /> Out</>
                : <><ArrowLeft size={10} className="inline" /> Return</>}
            </span>
            {t.status !== "scheduled" && (
              <span className={`text-[9px] font-semibold tracking-widest uppercase border px-1 py-0.5 ${t.status === "completed" ? "text-[#5c97f7] border-[#5c97f7]/40" : "text-[#515E7B] border-[#515E7B]/30"}`} data-testid={`ops-trip-status-${t.id}`}>
                {t.status}
              </span>
            )}
            {t.is_first_ride && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-widest text-[#ed6500] uppercase" data-testid={`ops-trip-first-${t.id}`}>
                <Sparkles size={10} /> First ride
              </span>
            )}
            {t.is_test && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-widest text-[#5c97f7] uppercase border border-[#5c97f7]/40 px-1 py-0.5" data-testid={`ops-trip-test-${t.id}`}>
                <FlaskConical size={9} /> Test
              </span>
            )}
          </div>
          <div className="mt-2 text-sm font-semibold truncate" data-testid={`ops-trip-rider-${t.id}`}>{t.rider_name || "—"}</div>
          <div className="text-xs text-[#515E7B] mt-1 flex items-start gap-1">
            <MapPin size={10} className="mt-0.5 shrink-0" />
            <span className="truncate">{t.pickup_address} → {t.dropoff_address}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
            <span className={`px-1.5 py-0.5 border hairline ${t.payment_status === "paid" ? "text-[#5c97f7] border-[#5c97f7]/40" : "text-[#ed6500] border-[#ed6500]/40"} uppercase tracking-widest font-semibold`}>
              {t.payment_status || "unpaid"}
            </span>
            {t.is_one_time && <span className="px-1.5 py-0.5 border hairline text-[#ed6500] border-[#ed6500]/40 uppercase tracking-widest font-semibold">One-time</span>}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0" onClick={stop}>
          {!readOnlyActions && t.rider_phone && (
            <a
              href={`tel:${t.rider_phone}`}
              onClick={stop}
              className="inline-flex items-center justify-center w-9 h-9 border hairline text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white transition-colors"
              aria-label={`Call ${t.rider_name || "rider"}`}
              data-testid={`ops-trip-call-${t.id}`}
            >
              <Phone size={14} />
            </a>
          )}
          {!readOnlyActions && (
            <StatusToggleButtons
              status={t.status}
              busy={busy}
              tripId={t.id}
              onComplete={(e) => { stop(e); onComplete && onComplete(); }}
              onCancel={(e) => { stop(e); onCancel && onCancel(); }}
              onReset={(e) => { stop(e); onReset && onReset(); }}
            />
          )}
          <button
            onClick={(e) => { stop(e); onTestFlag && onTestFlag(); }} disabled={busy}
            title={t.is_test ? "Remove test flag" : "Mark as test data"}
            className={`inline-flex items-center justify-center w-9 h-9 border hairline ${t.is_test ? "bg-[#5c97f7] text-white" : "text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white"} disabled:opacity-40 transition-colors`}
            aria-label={t.is_test ? "Remove test flag" : "Mark as test"}
            data-testid={`ops-trip-test-flag-${t.id}`}
          >
            <FlaskConical size={14} />
          </button>
          <button
            onClick={(e) => { stop(e); onDelete && onDelete(); }} disabled={busy}
            className="inline-flex items-center justify-center w-9 h-9 border hairline text-[#ed6500] hover:bg-[#ed6500] hover:text-white disabled:opacity-40 transition-colors"
            aria-label="Delete booking"
            data-testid={`ops-trip-delete-${t.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================  RIDE DETAIL DRAWER  ===================== */
function RideDetailDrawer({ trip, busy, onClose, onComplete, onCancel, onReset }) {
  const [drivers, setDrivers] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!trip) return;
    let cancelled = false;
    api.get("/admin/users/drivers").then((r) => { if (!cancelled) setDrivers(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [trip]);

  if (!trip) return null;
  const t = trip;
  const isCancelled = t.status === "cancelled";
  const isCompleted = t.status === "completed";

  const assign = async (driverId) => {
    setAssigning(true);
    try {
      await api.post(`/admin/trips/${t.id}/assign-driver`, { driver_id: driverId || null });
      toast.success(driverId ? "Driver assigned" : "Driver unassigned");
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not assign");
    } finally {
      setAssigning(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1128]/60 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="ops-ride-detail-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-[#F9F7F4] sm:border hairline animate-drawer-up sm:animate-none"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
        data-testid="ops-ride-detail"
      >
        {/* mobile drag handle */}
        <div className="sm:hidden pt-3 flex justify-center" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-[#515E7B]/30" />
        </div>

        <header className="px-5 sm:px-6 pt-4 pb-3 border-b hairline flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-xs text-[#ed6500]">Ride detail</div>
            <div className="font-serif text-3xl mt-1 truncate" data-testid="ops-detail-time">{formatTime12(t.pickup_time)}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px]">
              <span className="label-xs flex items-center gap-1">
                {t.direction === "outbound" ? <><ArrowRight size={11} className="inline" /> Out</> : <><ArrowLeft size={11} className="inline" /> Return</>}
              </span>
              <span className={`px-1.5 py-0.5 border tracking-widest uppercase font-semibold ${
                isCompleted ? "text-[#5c97f7] border-[#5c97f7]/40"
                : isCancelled ? "text-[#515E7B] border-[#515E7B]/30"
                : "text-[#0A1128] border-[#0A1128]/30"
              }`} data-testid="ops-detail-status">
                {t.status || "scheduled"}
              </span>
              {t.is_first_ride && (
                <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-widest text-[#ed6500] uppercase" data-testid="ops-detail-first-ride">
                  <Sparkles size={10} /> First ride
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[#515E7B] hover:text-[#0A1128] shrink-0"
            aria-label="Close"
            data-testid="ops-detail-close"
          ><ChevronDown className="sm:hidden" size={18} /><X className="hidden sm:inline" size={16} /></button>
        </header>

        {/* Rider */}
        <div className="px-5 sm:px-6 py-4 border-b hairline" data-testid="ops-detail-rider">
          <div className="label-xs text-[#515E7B]">Rider</div>
          <div className="font-serif text-xl mt-0.5 truncate" data-testid="ops-detail-rider-name">{t.rider_name || "—"}</div>
          {t.rider_email && <div className="text-xs text-[#515E7B] mt-0.5 truncate">{t.rider_email}</div>}
          {t.rider_phone && (
            <a
              href={`tel:${t.rider_phone}`}
              className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white px-3 py-2 transition-colors"
              data-testid="ops-detail-call"
            >
              <Phone size={12} /> Call · {t.rider_phone}
            </a>
          )}
        </div>

        {/* Route */}
        <div className="px-5 sm:px-6 py-4 border-b hairline" data-testid="ops-detail-route">
          <div className="label-xs text-[#515E7B]">Route</div>
          <div className="mt-2 flex items-start gap-2">
            <span className="mt-1 w-2 h-2 rounded-full bg-[#5c97f7] shrink-0" />
            <div className="text-sm text-[#0A1128] min-w-0">
              <div className="text-[10px] tracking-widest uppercase text-[#515E7B]">Pickup</div>
              <div className="break-words" data-testid="ops-detail-pickup">{t.pickup_address || "—"}</div>
            </div>
          </div>
          <div className="mt-2 flex items-start gap-2">
            <span className="mt-1 w-2 h-2 rounded-full bg-[#ed6500] shrink-0" />
            <div className="text-sm text-[#0A1128] min-w-0">
              <div className="text-[10px] tracking-widest uppercase text-[#515E7B]">Dropoff</div>
              <div className="break-words" data-testid="ops-detail-dropoff">{t.dropoff_address || "—"}</div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="px-5 sm:px-6 py-4 grid grid-cols-2 gap-3 border-b hairline" data-testid="ops-detail-meta">
          <Meta label="Tier"          value={t.route_tier_name || t.tier_name || (t.tier ? t.tier : "—")} testId="ops-detail-tier" />
          <Meta label="Type"          value={t.is_one_time ? "One-time" : "Subscription"} testId="ops-detail-paytype" />
          <Meta label="Payment"       value={(t.payment_status || "unpaid").toUpperCase()}
                tone={t.payment_status === "paid" ? "blue" : "orange"} testId="ops-detail-payment" />
          <Meta label="Direction"     value={t.direction === "outbound" ? "Outbound" : "Return"} testId="ops-detail-direction" />
        </div>

        {/* Driver assignment */}
        {!isCancelled && !isCompleted && (
          <div className="px-5 sm:px-6 py-4 border-b hairline" data-testid="ops-detail-driver-assign">
            <div className="label-xs text-[#515E7B] mb-2">Driver</div>
            {drivers.length === 0 ? (
              <p className="text-xs text-[#515E7B]">No drivers yet — promote a user from the Customers tab.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {drivers.map((d) => {
                  const active = t.driver_id === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => assign(active ? null : d.id)}
                      disabled={assigning}
                      className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-semibold border hairline rounded-full transition-colors disabled:opacity-50 ${
                        active ? "bg-[#0074d4] text-white border-[#0074d4]" : "bg-white text-[#515E7B] hover:text-[#0074d4]"
                      }`}
                      data-testid={`ops-assign-driver-${d.id}`}
                    >
                      {d.name || d.email}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {!isCancelled && !isCompleted && (
          <div className="px-5 sm:px-6 py-4 flex items-center gap-2" data-testid="ops-detail-actions">
            <button
              onClick={() => onComplete && onComplete(t.id)}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#5c97f7] hover:bg-[#5c97f7] hover:text-white px-4 py-3 disabled:opacity-50 transition-colors"
              data-testid="ops-detail-complete"
            >
              <CheckCircle2 size={12} /> Mark complete
            </button>
            <button
              onClick={() => onCancel && onCancel(t.id)}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#ed6500] hover:bg-[#ed6500] hover:text-white px-4 py-3 disabled:opacity-50 transition-colors"
              data-testid="ops-detail-cancel"
            >
              <XCircle size={12} /> Cancel ride
            </button>
          </div>
        )}
        {(isCancelled || isCompleted) && onReset && (
          <div className="px-5 sm:px-6 py-4" data-testid="ops-detail-actions">
            <button
              onClick={() => onReset(t.id)}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 text-[10px] tracking-widest uppercase font-semibold border hairline bg-white text-[#515E7B] hover:bg-[#0A1128] hover:text-white px-4 py-3 disabled:opacity-50 transition-colors"
              data-testid="ops-detail-reset"
            >
              <RotateCcw size={12} /> Revert to scheduled
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes drawer-up { from { transform: translateY(40px); opacity: 0.6; } to { transform: translateY(0); opacity: 1; } }
        .animate-drawer-up { animation: drawer-up 220ms ease-out; }
      `}</style>
    </div>
  );
}

function Meta({ label, value, tone, testId }) {
  const toneCls = tone === "blue" ? "text-[#5c97f7]" : tone === "orange" ? "text-[#ed6500]" : "text-[#0A1128]";
  return (
    <div data-testid={testId}>
      <div className="label-xs text-[#515E7B]">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${toneCls}`}>{value || "—"}</div>
    </div>
  );
}

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function shiftIso(iso, deltaDays) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + deltaDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
